import { CLIENT_NOTE_MAX_CHARS, type ClientNoteDto } from '@fos/shared/contracts/client-notes';
import { CLIENT_NOTE_SOURCE_LABELS } from '@fos/shared/labels';
import { Archive, ArchiveRestore, History, MoreHorizontal, Pencil, Pin, PinOff } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDateTimeShort, formatRelativePast } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import {
  Avatar,
  Button,
  Dialog,
  FormMessage,
  IconButton,
  ListSkeleton,
  Markdown,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Select,
  Status,
  Textarea,
  toast,
} from '../../ui';

/**
 * Notatki klienta (Fala 10): pole „Dodaj notatkę…” (Markdown, opcjonalne powiązanie z projektem / zadaniem /
 * spotkaniem, przypięcie), karta notatki (autor, data, źródło, powiązania, przypięta), edycja z historią wersji,
 * archiwizacja. Uprawnienia rozstrzyga serwer (`history.notes.*`); UI tylko ukrywa przyciski (`canEdit`, `canPin`).
 */

/** Odświeżenie list po zmianie notatki (oś czasu, „Ostatnie notatki”). */
export function useNotesInvalidate() {
  const utils = trpc.useUtils();
  return () =>
    Promise.all([utils.history.clientTimeline.invalidate(), utils.history.notes.list.invalidate()]).then(
      () => undefined,
    );
}

const NO_LINK = 'none';

function useLinkOptions(clientId: string, enabled: boolean) {
  const access = useAccess();
  const projects = trpc.projects.options.useQuery(
    { clientId },
    { enabled: enabled && access.can('projects'), staleTime: 60_000 },
  );
  const tasks = trpc.tasks.list.useQuery(
    { clientId, status: ['todo', 'in_progress', 'blocked', 'review'], sort: 'due', limit: 30 },
    { enabled: enabled && access.can('tasks'), staleTime: 60_000 },
  );
  const meetings = trpc.meetings.list.useQuery(
    { clientId, when: 'past', limit: 15 },
    { enabled: enabled && access.can('clients'), staleTime: 60_000 },
  );
  return [
    { value: NO_LINK, label: 'Bez powiązania' },
    ...(projects.data ?? []).map((p) => ({ value: `project:${p.id}`, label: `Projekt: ${p.name}` })),
    ...(tasks.data?.items ?? []).map((t) => ({ value: `task:${t.id}`, label: `Zadanie #${t.number} ${t.title}` })),
    ...(meetings.data?.items ?? []).map((m) => ({ value: `meeting:${m.id}`, label: `Spotkanie: ${m.title}` })),
  ];
}

function linkInput(value: string): { projectId?: string; taskId?: string; meetingId?: string } {
  const [kind, id] = value.split(':');
  if (!id) return {};
  if (kind === 'project') return { projectId: id };
  if (kind === 'task') return { taskId: id };
  if (kind === 'meeting') return { meetingId: id };
  return {};
}

/** Pole „Dodaj notatkę…” (zakładka Historia, Przegląd). Ctrl/⌘ + Enter zapisuje. */
export function NoteComposer({
  clientId,
  className,
  onAdded,
}: {
  clientId: string;
  className?: string;
  onAdded?: (note: ClientNoteDto) => void;
}) {
  const [body, setBody] = useState('');
  const [focused, setFocused] = useState(false);
  const [link, setLink] = useState(NO_LINK);
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const add = trpc.history.notes.add.useMutation();
  const invalidate = useNotesInvalidate();
  const expanded = focused || body.trim().length > 0;
  const options = useLinkOptions(clientId, expanded);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    const text = body.trim();
    if (!text || add.isPending) return;
    setError(null);
    try {
      const note = await add.mutateAsync({ clientId, bodyMd: text, ...linkInput(link), ...(pinned ? { pinned } : {}) });
      setBody('');
      setLink(NO_LINK);
      setPinned(false);
      void invalidate();
      toast.success('Dodano notatkę');
      onAdded?.(note);
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się dodać notatki.'));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className={cn('flex flex-col gap-3', className)} aria-label="Nowa notatka">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        rows={expanded ? 4 : 1}
        maxLength={CLIENT_NOTE_MAX_CHARS}
        aria-label="Treść notatki"
        placeholder="Dodaj notatkę…"
        className="min-h-10 resize-none"
      />
      {expanded && (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={link}
            onValueChange={setLink}
            options={options}
            size="s"
            aria-label="Powiązanie notatki"
            className="w-full sm:w-64"
          />
          <Button
            type="button"
            size="s"
            variant={pinned ? 'secondary' : 'ghost'}
            icon={pinned ? Pin : PinOff}
            aria-pressed={pinned}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setPinned((v) => !v)}
          >
            {pinned ? 'Przypięta' : 'Przypnij'}
          </Button>
          <span className="flex-1" />
          <span className="hidden text-caption text-fg-muted sm:inline">Markdown · Ctrl+Enter zapisuje</span>
          <Button type="submit" size="s" variant="primary" disabled={!body.trim()} loading={add.isPending}>
            Dodaj notatkę
          </Button>
        </div>
      )}
      {error && <FormMessage>{error}</FormMessage>}
    </form>
  );
}

function authorName(note: ClientNoteDto): string {
  if (note.author) return note.author.name;
  return CLIENT_NOTE_SOURCE_LABELS[note.source];
}

/** Karta notatki: autor, data, źródło, przypięcie, treść Markdown, powiązania, menu działań. */
export function NoteCard({ note, clamp = false }: { note: ClientNoteDto; clamp?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [versions, setVersions] = useState(false);
  const pin = trpc.history.notes.pin.useMutation();
  const archive = trpc.history.notes.archive.useMutation();
  const invalidate = useNotesInvalidate();
  const now = new Date();
  const ai = note.source === 'copilot' || note.source === 'mcp';

  const run = async (p: Promise<unknown>, ok: string) => {
    try {
      await p;
      void invalidate();
      toast.success(ok);
    } catch (err) {
      toast.error(errorMessage(err, 'Nie udało się zapisać zmiany.'));
    }
  };

  return (
    <article className="flex gap-3" aria-label={`Notatka: ${authorName(note)}`} id={`notatka-${note.id}`}>
      {note.author && !ai ? (
        <Avatar initials={note.author.initials} color={note.author.avatarColor} title={note.author.name} />
      ) : (
        <Avatar ai={ai} initials="A" title={authorName(note)} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-body-s font-medium text-fg">{authorName(note)}</span>
          <time
            className="text-caption text-fg-muted"
            dateTime={new Date(note.createdAt).toISOString()}
            title={formatDateTimeShort(note.createdAt, now)}
          >
            {formatRelativePast(note.createdAt, now)}
          </time>
          {note.editedAt && (
            <span className="text-caption text-fg-muted" title={formatDateTimeShort(note.editedAt, now)}>
              · edytowano
            </span>
          )}
          {note.source !== 'manual' && <Status tone="accent">{CLIENT_NOTE_SOURCE_LABELS[note.source]}</Status>}
          {note.pinned && (
            <span className="inline-flex items-center gap-1 text-caption font-medium text-fg">
              <Pin size={12} strokeWidth={1.75} aria-hidden />
              Przypięta
            </span>
          )}
          {note.archivedAt && <Status tone="neutral">W archiwum</Status>}
          <span className="flex-1" />
          {(note.canEdit || note.canPin || note.versionNo > 1) && (
            <Menu>
              <MenuTrigger asChild>
                <IconButton icon={MoreHorizontal} label="Działania notatki" size={28} iconSize={16} />
              </MenuTrigger>
              <MenuContent align="end">
                {note.canEdit && (
                  <MenuItem icon={Pencil} onSelect={() => setEditing(true)}>
                    Edytuj
                  </MenuItem>
                )}
                {note.canPin && (
                  <MenuItem
                    icon={note.pinned ? PinOff : Pin}
                    onSelect={() =>
                      void run(
                        pin.mutateAsync({ id: note.id, pinned: !note.pinned }),
                        note.pinned ? 'Odpięto notatkę' : 'Przypięto notatkę',
                      )
                    }
                  >
                    {note.pinned ? 'Odepnij' : 'Przypnij'}
                  </MenuItem>
                )}
                <MenuItem icon={History} onSelect={() => setVersions(true)}>
                  Historia wersji
                </MenuItem>
                {note.canEdit && (
                  <>
                    <MenuSeparator />
                    <MenuItem
                      icon={note.archivedAt ? ArchiveRestore : Archive}
                      onSelect={() =>
                        void run(
                          archive.mutateAsync({ id: note.id, archived: !note.archivedAt }),
                          note.archivedAt ? 'Przywrócono notatkę' : 'Przeniesiono notatkę do archiwum',
                        )
                      }
                    >
                      {note.archivedAt ? 'Przywróć' : 'Przenieś do archiwum'}
                    </MenuItem>
                  </>
                )}
              </MenuContent>
            </Menu>
          )}
        </div>
        <Markdown className={cn('mt-1 text-body-s break-words', clamp && 'line-clamp-4')}>{note.bodyMd}</Markdown>
        <NoteLinks note={note} />
      </div>
      {editing && <NoteEditDialog note={note} onClose={() => setEditing(false)} />}
      {versions && <NoteVersionsDialog note={note} onClose={() => setVersions(false)} />}
    </article>
  );
}

function NoteLinks({ note }: { note: ClientNoteDto }) {
  const links = [
    note.project && { to: `/projekty/${note.project.id}`, label: `Projekt: ${note.project.name}` },
    note.task && { to: `/zadania/${note.task.id}`, label: `Zadanie #${note.task.number} ${note.task.title}` },
    note.meeting && { to: `/spotkania/${note.meeting.id}`, label: `Spotkanie: ${note.meeting.title}` },
  ].filter((l): l is { to: string; label: string } => Boolean(l));
  if (links.length === 0) return null;
  return (
    <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-caption">
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="text-accent hover:underline">
          {l.label}
        </Link>
      ))}
    </p>
  );
}

function NoteEditDialog({ note, onClose }: { note: ClientNoteDto; onClose: () => void }) {
  const [body, setBody] = useState(note.bodyMd);
  const [error, setError] = useState<string | null>(null);
  const update = trpc.history.notes.update.useMutation();
  const invalidate = useNotesInvalidate();
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setError(null);
    try {
      await update.mutateAsync({ id: note.id, version: note.version, bodyMd: text });
      void invalidate();
      toast.success('Zapisano notatkę');
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać notatki.'));
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Edytuj notatkę"
      description="Poprzednia treść zostanie w historii wersji."
      width="l"
    >
      <form onSubmit={(e) => void save(e)} className="mt-4 flex flex-col gap-4">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          maxLength={CLIENT_NOTE_MAX_CHARS}
          aria-label="Treść notatki"
          autoFocus
        />
        {error && <FormMessage>{error}</FormMessage>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="primary" loading={update.isPending} disabled={!body.trim()}>
            Zapisz
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function NoteVersionsDialog({ note, onClose }: { note: ClientNoteDto; onClose: () => void }) {
  const versions = trpc.history.notes.versions.useQuery({ id: note.id });
  const now = new Date();
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Historia wersji notatki"
      width="l"
    >
      <div className="mt-4">
        {versions.isPending ? (
          <ListSkeleton rows={2} />
        ) : versions.isError ? (
          <FormMessage>Nie udało się wczytać wersji.</FormMessage>
        ) : (
          <ol className="flex flex-col" aria-label="Wersje notatki">
            {versions.data.map((v) => (
              <li key={v.versionNo} className="border-b border-line py-3 last:border-b-0">
                <p className="text-caption text-fg-muted">
                  Wersja {v.versionNo} · {v.editedBy?.name ?? CLIENT_NOTE_SOURCE_LABELS[v.source]} ·{' '}
                  {formatDateTimeShort(v.createdAt, now)}
                  {v.source !== 'manual' ? ` · ${CLIENT_NOTE_SOURCE_LABELS[v.source]}` : ''}
                </p>
                <Markdown className="mt-1 text-body-s break-words">{v.bodyMd}</Markdown>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Dialog>
  );
}
